import React, { useEffect } from 'react';
import { useWindowSize } from 'react-use';
import { CloseButton } from '@noths/polaris-client-ribbons-design-system';
import { pxToRem, theme } from '@noths/polaris-client-styles';
import { SerializedError } from '@reduxjs/toolkit';
import { NextRouter, useRouter } from 'next/router';

import { styles as modalStyles } from 'src/components/molecules/Modal';
import { DisplayWhenMediumAndDown } from 'src/styles/Media';
import * as copy from '../constants/copy';
import { DESKTOP_FILTER_HEIGHT_CSS_VAR } from '../constants/cssVariables';
import { FILTER_LIST_CLOSE_BUTTON } from '../constants/elementIds';
import { FilterWithFilterOptionsCount } from '../modules/selectors';
import { FilterControls } from './FilterControls';
import { FilterListItem } from './FilterListItem';
import { ListingsApiError } from './ListingsApiError';
import * as styles from '../styles/FilterList.styles';

interface Props {
  isLoading: boolean;
  filters: FilterWithFilterOptionsCount[];
  onCloseFilters: VoidFunction;
  onViewResults: VoidFunction;
  totalSelectedFilterOptionsCount: number;
  totalProducts: number;
  onResetFilters: (router: NextRouter) => void;
  error?: SerializedError | null;
}

const setFilterHeight = (windowHeight: number) =>
  document.documentElement.style.setProperty(
    DESKTOP_FILTER_HEIGHT_CSS_VAR,
    `calc(${windowHeight}px - ${pxToRem(theme.spacing.xl)})`,
  );

export const FilterList = ({
  isLoading,
  filters,
  onCloseFilters,
  onViewResults,
  onResetFilters,
  totalSelectedFilterOptionsCount,
  totalProducts,
  error,
}: Props) => {
  const router = useRouter();
  const { height } = useWindowSize();

  useEffect(() => {
    setFilterHeight(height);
  }, [height]);

  return (
    <form
      css={[modalStyles.modalViewport, styles.filterListWrapper]}
      onSubmit={(e) => e.preventDefault()}
    >
      <DisplayWhenMediumAndDown>
        <header css={modalStyles.modalSectionHeader}>
          <CloseButton
            testId={FILTER_LIST_CLOSE_BUTTON}
            size="large"
            aria-label="Close"
            onClick={onCloseFilters}
          />
          <h2>{copy.FILTER_LIST_TITLE}</h2>
        </header>
      </DisplayWhenMediumAndDown>
      {error ? (
        <ListingsApiError />
      ) : (
        <ul css={[styles.list, modalStyles.modalScrollableContent]}>
          {filters.map(
            (filter) =>
              filter.options.length > 0 && <FilterListItem filter={filter} key={filter.uid} />,
          )}
        </ul>
      )}
      <DisplayWhenMediumAndDown>
        <FilterControls
          isLoading={isLoading}
          totalProducts={totalProducts}
          totalSelectedFilterOptionsCount={totalSelectedFilterOptionsCount}
          onViewResults={onViewResults}
          resetText={copy.FILTER_LIST_RESET}
          error={error}
          onReset={() => onResetFilters(router)}
        />
      </DisplayWhenMediumAndDown>
    </form>
  );
};
